import { useModuleEnabled } from "../access/useFeatureAccess";
import PpLoginModal from "../auth/PpLoginModal";
import { featureFlags } from "../config/featureFlags";
import { useWorklistAccessGate } from "../worklist/useWorklistAccessGate";

/**
 * Home-page prompt to connect the provider's Provider Portal account.
 *
 * WHY HOME NOW ASKS. Home's widgets (UrgentPatientsWidget /
 * HospitalEdEventsWidget) are both powered by PP-API and render NOTHING until
 * `useWorklistAccessGate` reaches `"ready"`. For a provider who has never
 * linked a PP account that is every visit — so the landing page came up
 * completely empty with no way to fix it, and no hint that a sign-in was the
 * missing piece. This card is that hint: it is the ONE gate state Home
 * surfaces, and it is actionable.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. Only `"no-pp-session"` renders anything.
 * The rest of the gate ladder — `resolving-ipa`, `ipa-error`,
 * `ipa-forbidden`, `no-ipa`, `no-npi` — stays silent exactly as before: a
 * landing page must not explain an IPA fetch failure, or demand an NPI, for a
 * card the provider did not ask for. `/worklist` owns the full ladder.
 *
 * It also stays behind the SAME preconditions as the widgets it unblocks
 * (`patientPriorityIndex` flag + the `PriorityWorklist` persona module). With
 * the widgets off there is no PP-backed content on Home, so there is nothing
 * to sign in FOR.
 */
export default function HomePpConnectPrompt() {
  const worklistEnabled = useModuleEnabled("PriorityWorklist");
  if (!featureFlags.patientPriorityIndex || !worklistEnabled) return null;
  return <HomePpConnectPromptGated />;
}

function HomePpConnectPromptGated() {
  const accessGate = useWorklistAccessGate();

  // Anything other than "no PP account yet" is Home's business to stay quiet
  // about — including `"ready"`, where the widgets themselves take over.
  if (accessGate.status !== "no-pp-session") return null;

  return (
    <>
      {/* This prompt is the only occupant of Home's `lg:grid-cols-2` row —
          it renders precisely WHEN the two cards that share that row do not
          (see HomePage.tsx). Left in a single column it sat hard against the
          left edge with dead space beside it, so it spans BOTH columns and
          centers itself, capped at `max-w-2xl` so it reads as a centered
          card rather than a full-bleed banner.

          These classes live here rather than on a wrapper in HomePage on
          purpose: this component returns `null` in the connected case, and a
          wrapper would leave an empty grid cell behind that shunts
          UrgentPatientsCard into the second column. */}
      <div
        data-testid="home-pp-connect-prompt"
        className="rounded-2xl border border-dashed border-border bg-card/50 p-6 flex flex-col items-center gap-3 text-center mx-auto w-full max-w-2xl lg:col-span-2"
      >
        <h2 className="text-base font-bold text-foreground m-0">
          Log in to Provider Portal to see your patients
        </h2>
        <p className="text-sm text-muted-foreground m-0">
          Your urgent patients and hospital &amp; ED activity are powered by
          Provider Portal. Sign in to fill in your home page.
        </p>
        {accessGate.identityEmail ? (
          <button
            type="button"
            onClick={accessGate.requestPpLogin}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            Log in to Provider Portal
          </button>
        ) : (
          <p role="alert" className="text-sm text-destructive font-medium m-0">
            Unable to determine your sign-in identity — please refresh and try
            again.
          </p>
        )}
      </div>
      {/* PpLoginModal portals itself to <body>, so mounting it from inside the
          widget grid is safe — it is not laid out as a grid child. */}
      {accessGate.ppLoginOpen && accessGate.identityEmail && (
        <PpLoginModal
          email={accessGate.identityEmail}
          onSuccess={accessGate.handlePpLoginSuccess}
          onCancel={accessGate.cancelPpLogin}
        />
      )}
    </>
  );
}
